// @eidollon/contracts/saved-screens
//
// Saved screens (a.k.a. "saved views") — per-user, per-tenant snapshots of an
// EntityScreen's filter state, column config, sort, and (for views with at
// least one geo-polygon) AOI alert toggle.
//
// One contract used by every entity browser (Intel today; Vendor / Agency /
// Award browsers tomorrow). Per CLAUDE.md Iron Rule #11 the SavedView UI
// primitive lives in `packages/ui-filters` and consumes these schemas.
//
// Wire shapes:
//   GET    /v1/screens                   → SavedScreenListResponse
//   POST   /v1/screens                   ← SavedScreenCreateBody         → { id }
//   DELETE /v1/screens/:id
//   POST   /v1/saved-screens/:id/aoi-alert ← AoiAlertToggleBody          → AoiAlertToggleResponse

import { z } from 'zod';
import { FilterState } from './filters.js';

/* ──────────────────────────────────────────────────────────────────────────
 *  Wire types — what the API returns + accepts.
 * ────────────────────────────────────────────────────────────────────────── */

/** Compact summary returned by GET /v1/screens. The full filter blob comes
 *  along so the client can hydrate FilterState on load without a second
 *  round-trip. */
// Not `.strict()` — the wire is allowed to carry extra fields (e.g. legacy
// `last_run_at` from the existing terminal landing page) so older surfaces
// don't break when a newer SavedView consumer ships first.
export const SavedScreenSummary = z
  .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    description: z.string().max(500).nullable().optional(),
    filters: FilterState,
    // The rail's "Alert me" checkbox lights up only when the saved view has
    // at least one polygon. The summary echoes the count so the client never
    // has to reach into filters.aoi.polygons.length itself.
    aoi_polygon_count: z.number().int().nonnegative().default(0),
    aoi_alert_active: z.boolean().default(false),
    is_shared: z.boolean().default(false),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .passthrough();
export type SavedScreenSummary = z.infer<typeof SavedScreenSummary>;

export const SavedScreenListResponse = z
  .object({
    items: z.array(SavedScreenSummary),
  })
  .strict();
export type SavedScreenListResponse = z.infer<typeof SavedScreenListResponse>;

export const SavedScreenCreateBody = z
  .object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    filters: FilterState.default({}),
    is_shared: z.boolean().default(false),
  })
  .strict();
export type SavedScreenCreateBody = z.infer<typeof SavedScreenCreateBody>;

export const SavedScreenCreateResponse = z
  .object({
    id: z.string().min(1).max(64),
  })
  .strict();
export type SavedScreenCreateResponse = z.infer<typeof SavedScreenCreateResponse>;

export const AoiAlertToggleBody = z
  .object({
    active: z.boolean(),
  })
  .strict();
export type AoiAlertToggleBody = z.infer<typeof AoiAlertToggleBody>;

export const AoiAlertToggleResponse = z
  .object({
    ok: z.literal(true),
    active: z.boolean(),
    polygon_count: z.number().int().nonnegative(),
  })
  .strict();
export type AoiAlertToggleResponse = z.infer<typeof AoiAlertToggleResponse>;
